import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { EditorModule } from 'primeng/editor';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { finalize, switchMap } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthArticlePublishingService } from './growth-article-publishing.service';
import { buildStorefrontArticleUrl, slugifyArticleTitle } from './growth-article-publishing.util';
import { GrowthContentAsset, GrowthMarketingService } from './growth-marketing.service';
import { CONTENT_STATUS_I18N_KEYS, normalizeContentStatus } from './growth-status.util';

type BodyFormat = 'markdown' | 'html';

@Component({
  standalone: true,
  selector: 'ain-growth-article-publishing-detail',
  imports: [
    FormsModule,
    RouterLink,
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    ButtonModule,
    EditorModule,
    InputTextModule,
    SelectButtonModule,
    TextareaModule,
    ToastModule
  ],
  providers: [MessageService],
  template: `
    <p-toast />
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.articlePublishing.reviewList.title')"
      [description]="t('growth.articlePublishing.reviewDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.articlePublishing.reviewDetail.hint.title'"
        [descriptionKey]="'growth.articlePublishing.reviewDetail.hint.description'"
        [bulletKeys]="[
          'growth.articlePublishing.reviewDetail.hint.bullets.edit',
          'growth.articlePublishing.reviewDetail.hint.bullets.format',
          'growth.articlePublishing.reviewDetail.hint.bullets.publish',
          'growth.articlePublishing.reviewDetail.hint.bullets.improve'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!content()) {
        <ain-state type="empty" [title]="t('growth.marketing.reviewQueue.empty')" />
      } @else {
        <section class="card">
          <h3>{{ t('growth.marketing.reviewQueue.sections.item') }}</h3>
          <p class="meta">
            {{ t('growth.marketing.reviewQueue.columns.status') }}:
            <strong>{{ contentStatusLabel(content()!.status) }}</strong>
          </p>

          <div class="grid">
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.contentTitle')">
              <input pInputText [(ngModel)]="editModel.title" (ngModelChange)="onTitleChanged()" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.articlePublishing.fields.slug')">
              <input pInputText [(ngModel)]="editModel.slug" (ngModelChange)="slugManuallyEdited = true" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.locale')">
              <input pInputText [(ngModel)]="editModel.locale" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.summary')">
              <textarea pInputTextarea rows="3" [(ngModel)]="editModel.summary"></textarea>
            </ain-form-field>
          </div>
          <ain-form-field [label]="t('growth.contentAsset.fields.bodyFormat')">
            <p-selectButton
              [options]="bodyFormatOptions"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="editModel.bodyFormat" />
          </ain-form-field>
          <ain-form-field class="body-field" [label]="t('growth.contentAsset.fields.body')">
            @if (editModel.bodyFormat === 'markdown') {
              <textarea pInputTextarea rows="8" [(ngModel)]="editModel.bodyMarkdown"></textarea>
            } @else {
              <p-editor [(ngModel)]="editModel.bodyMarkdown" [style]="{ height: '280px' }"></p-editor>
            }
          </ain-form-field>

          <div class="toolbar">
            <a routerLink="/growth/marketing/article-publishing" pButton severity="secondary" [label]="t('common.back')"></a>
            <a
              [routerLink]="['/growth/marketing/content-assets', content()!.id]"
              pButton
              severity="help"
              [outlined]="true"
              [label]="t('growth.contentAsset.next.editAsset')"></a>
            <a
              routerLink="/growth/marketing/ace-studio"
              pButton
              severity="secondary"
              [label]="t('growth.articlePublishing.actions.openAceStudio')"></a>
            <button pButton type="button" severity="secondary" [label]="t('common.save')" [loading]="saving()" (click)="saveContent()"></button>
            @if (canSubmitContent()) {
              <button pButton type="button" [label]="t('growth.marketing.campaigns.actions.submitForReview')" [loading]="approving()" (click)="submitContent()"></button>
            }
            @if (canApproveContent()) {
              <button pButton type="button" [label]="t('growth.marketing.reviewQueue.actions.approve')" [loading]="approving()" (click)="approveContent()"></button>
            }
          </div>
        </section>

        <section class="card">
          <h3>{{ t('growth.articlePublishing.publish.title') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.articlePublishing.help.title'"
            [descriptionKey]="'growth.articlePublishing.help.description'" />
          <div class="toolbar">
            <button
              pButton
              type="button"
              severity="help"
              [label]="isPublished() ? t('growth.articlePublishing.actions.republish') : t('growth.articlePublishing.actions.publishAll')"
              [loading]="publishing()"
              [disabled]="!canPublishWebsite()"
              (click)="publishWebsite()"></button>
            <a
              [routerLink]="['/growth/marketing/publish-operations']"
              [queryParams]="{ contentId: content()!.id, channel: 'web-blog' }"
              pButton
              severity="secondary"
              [label]="t('growth.articlePublishing.actions.openPublishOps')"></a>
          </div>
        </section>

        @if (articleUrl()) {
          <section class="card success">
            <h3>{{ t('growth.articlePublishing.success.title') }}</h3>
            <p>{{ t('growth.articlePublishing.success.description') }}</p>
            <div class="toolbar">
              <a [href]="articleUrl()" target="_blank" rel="noopener" pButton [label]="t('growth.articlePublishing.actions.viewArticle')"></a>
              <a
                [routerLink]="['/growth/marketing/publish-operations']"
                [queryParams]="{ contentId: content()!.id, channel: 'web-blog' }"
                pButton
                severity="secondary"
                [label]="t('growth.articlePublishing.actions.openPublishOps')"></a>
            </div>
          </section>
        }
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-top: 12px; background: #fff; }
    .card.success { border-color: #86efac; background: #f0fdf4; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
    .meta { margin: 0 0 8px; color: #4b5563; }
    .body-field :host ::ng-deep .p-editor,
    .body-field :host ::ng-deep textarea { width: 100%; }
  `]
})
export class GrowthArticlePublishingDetailComponent implements OnInit {
  private readonly marketing = inject(GrowthMarketingService);
  private readonly articlePublishing = inject(GrowthArticlePublishingService);
  private readonly currentContext = inject(CurrentContextService);
  private readonly messages = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly content = signal<GrowthContentAsset | null>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly approving = signal(false);
  readonly publishing = signal(false);
  readonly articleUrl = signal<string | null>(null);

  editModel = {
    title: '',
    slug: '',
    locale: 'en-US',
    summary: '',
    bodyMarkdown: '',
    bodyFormat: 'markdown' as BodyFormat
  };

  get bodyFormatOptions(): Array<{ value: BodyFormat; label: string }> {
    return [
      { value: 'markdown', label: this.t('growth.contentAsset.fields.bodyFormat.markdown') },
      { value: 'html', label: this.t('growth.contentAsset.fields.bodyFormat.html') }
    ];
  }

  slugManuallyEdited = false;

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const contentId = params.get('contentId');
      if (!contentId) {
        this.content.set(null);
        this.articleUrl.set(null);
        return;
      }
      this.loadContent(contentId);
    });
  }

  saveContent(): void {
    const current = this.content();
    if (!current) {
      return;
    }

    this.saving.set(true);
    this.marketing.updateContent(current.id, this.toUpdatePayload(current)).pipe(
      finalize(() => this.saving.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: updated => this.applyContent(updated),
      error: () => this.toastError()
    });
  }

  submitContent(): void {
    const current = this.content();
    if (!current) {
      return;
    }

    this.approving.set(true);
    this.marketing.updateContent(current.id, this.toUpdatePayload(current)).pipe(
      switchMap(updated => this.marketing.submitContentForReview(updated.id)),
      finalize(() => this.approving.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: updated => this.applyContent(updated),
      error: () => this.toastError()
    });
  }

  approveContent(): void {
    const current = this.content();
    if (!current) {
      return;
    }

    this.approving.set(true);
    this.marketing.updateContent(current.id, this.toUpdatePayload(current)).pipe(
      switchMap(updated => {
        const status = normalizeContentStatus(updated.status);
        if (status === 'Approved' || status === 'Scheduled' || status === 'Published') {
          return this.marketing.getContent(updated.id);
        }
        if (status === 'InReview') {
          return this.marketing.approveContent(updated.id);
        }
        return this.marketing.submitContentForReview(updated.id).pipe(
          switchMap(() => this.marketing.approveContent(updated.id))
        );
      }),
      finalize(() => this.approving.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: updated => this.applyContent(updated),
      error: () => this.toastError()
    });
  }

  publishWebsite(): void {
    const current = this.content();
    if (!current || !this.canPublishWebsite()) {
      return;
    }

    this.publishing.set(true);
    this.marketing.updateContent(current.id, this.toUpdatePayload(current)).pipe(
      switchMap(updated => this.articlePublishing.publishToPublicStorefront(updated.id)),
      finalize(() => this.publishing.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: result => {
        this.applyContent(result.content);
        this.articleUrl.set(result.articleUrl);
        this.messages.add({
          severity: 'success',
          summary: this.t('growth.articlePublishing.success.title')
        });
      },
      error: () => this.toastError()
    });
  }

  canSubmitContent(): boolean {
    const status = normalizeContentStatus(this.content()?.status);
    return status === 'Draft' || status === 'AiGenerated' || status === 'Rejected';
  }

  canApproveContent(): boolean {
    return normalizeContentStatus(this.content()?.status) === 'InReview';
  }

  isPublished(): boolean {
    const status = normalizeContentStatus(this.content()?.status);
    return status === 'Published' || status === 'Scheduled';
  }

  canPublishWebsite(): boolean {
    const context = this.currentContext.context();
    const allowed = (context?.permissions ?? []).some(item => item.key === 'growth.marketing.publish');
    return allowed && Boolean(this.content()) && this.editModel.title.trim().length > 0;
  }

  onTitleChanged(): void {
    if (!this.slugManuallyEdited && !this.editModel.slug) {
      this.editModel.slug = slugifyArticleTitle(this.editModel.title);
    }
  }

  contentStatusLabel(status: unknown): string {
    const normalized = normalizeContentStatus(status);
    return this.t(CONTENT_STATUS_I18N_KEYS[normalized]);
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private loadContent(contentId: string): void {
    this.loading.set(true);
    this.articleUrl.set(null);
    this.marketing.getContent(contentId).pipe(
      finalize(() => this.loading.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: asset => this.applyContent(asset),
      error: () => {
        this.content.set(null);
        this.toastError();
      }
    });
  }

  private applyContent(asset: GrowthContentAsset): void {
    this.content.set(asset);
    this.editModel = {
      title: asset.title,
      slug: asset.slug ?? slugifyArticleTitle(asset.title),
      locale: asset.locale,
      summary: asset.summary ?? '',
      bodyMarkdown: asset.bodyMarkdown ?? '',
      bodyFormat: this.normalizeBodyFormat(asset.bodyFormat)
    };
    this.slugManuallyEdited = Boolean(asset.slug);
    const status = normalizeContentStatus(asset.status);
    if (status === 'Published' || status === 'Scheduled') {
      this.articleUrl.set(buildStorefrontArticleUrl(asset.slug ?? slugifyArticleTitle(asset.title)));
    }
  }

  private toastError(): void {
    this.messages.add({ severity: 'error', summary: this.t('common.error') });
  }

  private toUpdatePayload(current: GrowthContentAsset): Record<string, unknown> {
    return {
      id: current.id,
      title: this.editModel.title,
      contentType: current.contentType,
      ownerScope: current.ownerScope,
      locale: this.editModel.locale,
      slug: this.editModel.slug || slugifyArticleTitle(this.editModel.title),
      summary: this.editModel.summary,
      bodyMarkdown: this.editModel.bodyMarkdown,
      bodyFormat: this.editModel.bodyFormat,
      citationRefsJson: current.citationRefsJson,
      publishTargetsJson: current.publishTargetsJson,
      briefId: current.briefId ?? null
    };
  }

  private normalizeBodyFormat(value: string | null | undefined): BodyFormat {
    return value?.trim().toLowerCase() === 'html' ? 'html' : 'markdown';
  }
}
